import React, {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import PropTypes from 'prop-types'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {useDerivedProduct} from '../../../hooks/use-derived-product'
import LoadingSpinner from '../../../components/loading-spinner'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import useTagManager from '../../../hooks/use-tag-manager'
import {useGlobaleContext} from '../../../contexts/globale'
import {useAddToCartDrawerContext} from '../../../hooks/use-add-to-cart-drawer'
import useMonetate from '../../../hooks/use-monetate'
import {useSiteContext} from '../../../contexts/site'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {getPriceData} from '../../../utils/product-utils'
import {loadScriptOnce} from '../../../utils/script-utils'

const MirelzScript = ({scriptId, addToCart, product, customer, wishlist}) => {
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {countryISO, currencyCode} = useGlobaleContext()
    const {
        app: {commerceAPI}
    } = getConfig()
    const location = useLocation()
    const {onOpen: onAddToCartDrawerOpen, onClose: onAddToCartDrawerClose} =
        useAddToCartDrawerContext()
    const tagManager = useTagManager()
    const {setOpenBonusModal} = useSiteContext()
    const monetate = useMonetate()
    const [showAddToCartLoader, setShowAddToCartLoader] = useState(false)

    const {mirelz} = commerceAPI
    const {config} = mirelz || {}

    var mirelzEnable = false
    var mz360Enabled = false
    var mirelzAvailableCountries = null
    var mirelzExternalJsScriptPath = null
    var mz360Path = null

    useEffect(() => {
        onAddToCartDrawerClose()
    }, [location?.pathname])

    try {
        const jsonConfig = JSON.parse(config)
        mirelzEnable = jsonConfig.enableVTO
        mz360Enabled = jsonConfig.enable360
        mirelzAvailableCountries = jsonConfig.availableCountries
        mirelzExternalJsScriptPath = jsonConfig.vtoScriptPath
        mz360Path = jsonConfig.mz360ScriptPath
    } catch (e) {
        console.log(e.message)
    }

    const {variant} = useDerivedProduct(product, false, false)
    const canLoadMirelz = Boolean(
        mirelzAvailableCountries &&
            mirelzAvailableCountries.indexOf(countryISO) !== -1 &&
            mirelzEnable
    )
    const mirelzScriptSrc =
        scriptId === 'mzDefault'
            ? mirelzExternalJsScriptPath
            : scriptId === 'mz360' && mz360Enabled
            ? mz360Path
            : null

    useEffect(() => {
        if (!canLoadMirelz || !mirelzScriptSrc) return

        loadScriptOnce(mirelzScriptSrc)
    }, [canLoadMirelz, location.pathname, mirelzScriptSrc])

    return (
        <>
            <Box
                className={`mirelz-hidden-btn mirelz-hidden-btn-${scriptId}`}
                display="none"
                onClick={async () => {
                    try {
                        setShowAddToCartLoader(true)
                        const productIDs = window.mzProductIDs
                        const normalizeProductId = (id) =>
                            id?.indexOf('_') > -1 ? id.split('_')[1] : id
                        const rawIds = Array.isArray(productIDs) ? productIDs : [productIDs]
                        const ids = rawIds.map(normalizeProductId).filter(Boolean)

                        const token = await getTokenWhenReady()
                        const productParams = {
                            perPricebook: true,
                            expand: [
                                'availability',
                                'promotions',
                                'images',
                                'prices',
                                'variations'
                            ],
                            c_selectedCountry: countryISO,
                            c_selectedCurrency: currencyCode
                        }

                        const fetchedProducts = await Promise.all(
                            ids.map((id) =>
                                api.shopperProducts.getProduct({
                                    parameters: {id, ...productParams},
                                    headers: {Authorization: `Bearer ${token}`}
                                })
                            )
                        )

                        const items = ids.map((id, i) => {
                            const priceDataForItem = getPriceData(fetchedProducts[i])
                            return {
                                quantity: 1,
                                variant: {
                                    addByID: true,
                                    productId: id,
                                    price: priceDataForItem?.currentPrice
                                }
                            }
                        })

                        const response = await addToCart(items)
                        const {productSelectionValues: itemsAdded, basketResponse} = response || {}
                        setShowAddToCartLoader(false)

                        if (itemsAdded) {
                            const fetchedProduct = fetchedProducts[0]
                            const drawerPriceData = getPriceData(fetchedProduct)
                            const smallImageGroup = findImageGroupBy(fetchedProduct?.imageGroups, {
                                viewType: 'small'
                            })
                            const heroImage =
                                smallImageGroup?.images?.[0]?.disBaseLink ||
                                fetchedProduct?.c_images?.smallImage

                            tagManager.addToCart(
                                product,
                                variant,
                                null,
                                null,
                                document.querySelector('.mirelz-modal-360') ? 'mirelz' : 'vto'
                            )
                            tagManager.addToCartGA4({
                                itemsAdded: [{product: product}],
                                method: document.querySelector('.mirelz-modal-360')
                                    ? 'mirelz'
                                    : 'vto',
                                currency: currencyCode,
                                sorting: undefined
                            })
                            onAddToCartDrawerOpen({
                                product: fetchedProduct,
                                itemsAdded,
                                selectedQuantity: 1,
                                priceData: drawerPriceData,
                                heroImage
                            })
                            setOpenBonusModal(true)
                            monetate.monetateTrackPage(
                                'addToBagModal',
                                window.mtBreadcrumb,
                                basketResponse?.productItems,
                                customer?.customerType === 'registered',
                                wishlist?.customerProductListItems?.length || 0,
                                null,
                                null,
                                itemsAdded,
                                basketResponse?.giftCertificateItems
                            )
                            if (scriptId === 'mz360') {
                                document
                                    .querySelector('.mirelz-modal-360 .mz-close-button')
                                    ?.click()
                            }
                        }
                    } catch (e) {
                        delete window.mzProductIDs
                        setShowAddToCartLoader(false)
                    }

                    delete window.mzProductIDs
                }}
            ></Box>
            {showAddToCartLoader && <LoadingSpinner position="fixed" top="auto" zIndex="10000" />}
        </>
    )
}

MirelzScript.propTypes = {
    scriptId: PropTypes.string,
    addToCart: PropTypes.func,
    priceData: PropTypes.any,
    wishlist: PropTypes.any,
    customer: PropTypes.any,
    product: PropTypes.object
}

export default MirelzScript
